import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Grid, Button, TextField, Typography, MenuItem, Divider, Checkbox, Modal, Box, IconButton } from "@material-ui/core";
import PublicWrapper from "../public-wrapper-v2/public-wrapper";
import useContactInfoStyles from "./contact-info.styles";
import { useSelector, useDispatch } from "react-redux";
import { saveUserEmailAction, setUserAction } from "../../stores/user/user-action";
import Spacing from "../portico/spacing/spacing";
import CloseIcon from '@material-ui/icons/Close';
import { FeatureToggleContext } from "../../config/toggle.context";
import { FCB_PRIVACY_STATEMENT, TERMS_OF_USE } from '../../utils/urls'


const ContactInfo = () => {
  const user = useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  const classes = useContactInfoStyles();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [countryCode, setCountryCode] = useState('1');
  const [mobileNumber, setMobileNumber] = useState(user.mobileNumber.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3"));
  const [agreeTerm, setAgreeTerms] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const { useToggle } = useContext(FeatureToggleContext);
  const toggleSendEmailToNewUser = useToggle("ftSendEmailToNewUser");
  const sendEmailToNewUser = toggleSendEmailToNewUser(false, true);

  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }

  const query = useQuery();
  const email = query.get('email');

  useEffect(() => {
    if (!user.email && !sendEmailToNewUser) {
      navigate('/welcome');
    }
    else if (sendEmailToNewUser && email) {
      dispatch(saveUserEmailAction(email));
    } else {
      navigate('/welcome')
    }
  }, []);

  const onBackClick = () => {
    navigate(-1);
  };

  const onConfirm = () => {
    navigate("/in-usa");
    const numbers = mobileNumber.replace(/[^0-9]/g, "")
    dispatch(setUserAction({ ...user, firstName, lastName, countryCode, mobileNumber: numbers }))
  };

  const formatMobileNumber = (e) => {
    const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
    if (onlyNumbers.length < 10) {
      setMobileNumber(onlyNumbers);
    } else if (onlyNumbers.length === 10) {
      const number = onlyNumbers.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
      setMobileNumber(number);
    }
  };

  const isFormValid = firstName && lastName && countryCode && (mobileNumber && mobileNumber.length >= 10) && agreeTerm;

  return (
    <div className={classes.trapezoidBgContactInfo}>
      <PublicWrapper
        data-testid="publicWrapper"
        title=""
        displayPrivacyPolicyFooter={true}
        backgroundClass={classes.root}
        contentWrapperClass={classes.contactInfoWrapper}
      >
        <div className={classes.mainBox}>
          <div className={classes.title}>
            <Typography variant="h6" className={`${classes.subtitle}`}>
              Great! Provide us with your contact info
            </Typography>
          </div>
          <div className={classes.contentClass}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  label="First Name"
                  fullWidth
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value.replace(/[^a-z]/gi, ''))}
                  inputProps={{ "data-testid": "firstName" }}
                ></TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  label="Last Name"
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value.replace(/[^a-z]/gi, ''))}
                  inputProps={{ "data-testid": "lastName" }}
                ></TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Phone Country Code"
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={true}
                  className={classes.countryCode}
                  value={countryCode}
                >
                  <MenuItem value={1}>United States (+1)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  variant="outlined"
                  label="Mobile Number"
                  fullWidth
                  value={mobileNumber}
                  onChange={formatMobileNumber}
                  inputProps={{ "data-testid": "mobileNumber" }}
                ></TextField>
              </Grid>
              <Grid item xs={12}>
                <div className={classes.contentInfo}>
                  <p>
                    Should you need to return to your application at a later time, you’ll be asked to authenticate using both your email and phone.
                  </p>
                </div>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={2} md={1}>
                <Checkbox
                  checked={agreeTerm}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  color="primary"
                  data-testid="agreeTerms"
                />
              </Grid>
              <Grid item xs={10} md={11}>
                <div className={classes.agreeTerms}>
                  <p>
                    I acknowledge that I have read and agree to First Citizens Bank's <a href={TERMS_OF_USE} target="_blank" rel="noopener noreferrer">Terms of use</a> and <a href={FCB_PRIVACY_STATEMENT} target="_blank" rel="noopener noreferrer">Privacy Statement</a> and agree that First Citizens Bank may use any data I provide to them in determining what additional First Citizens Bank products and services may be of interest to my company. I understand that I can unsubscribe from marketing communications at any time. By consenting, I further agree and consent to the use of data, including access to any software or third-party data source as use for business process as outlined in the Privacy Statement and as may be used in this application.
                  </p>
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                {sendEmailToNewUser ? "" :
                  <Button color="primary" variant="outlined" onClick={onBackClick} className={classes.button}>
                    <span style={{ padding: "0 3rem" }}>Back</span>
                  </Button>}
              </Grid>
              <Grid item xs={12} md={6} className={classes.continueButton}>
                <Button
                  color="primary"
                  disabled={!isFormValid}
                  variant="contained"
                  onClick={handleOpen}
                  data-testid="continueButton"
                  className={classes.button}
                >
                  <span style={{ padding: "0 3rem" }}>Continue</span>
                </Button>
                <Modal
                  open={open}
                  aria-labelledby="modal-title"
                  aria-describedby="modal-description"
                  data-testid="confirmModal"
                >
                  <Box className={classes.modalBox}>
                    <IconButton aria-label="close" onClick={handleClose} style={{ float: "right" }}>
                      <CloseIcon />
                    </IconButton>
                    <div className={classes.boxContent}>
                      <Typography className={classes.contentText} id="modal-title" variant="h6">
                        Confirm Contact Info
                      </Typography>
                      <Spacing></Spacing>
                      <Typography id="modal-description">
                        Please confirm your details, including your mobile number, as they will be used to validate your account:
                      </Typography>
                      <Spacing></Spacing>
                      <Spacing></Spacing>
                      <Spacing></Spacing>

                      <Grid container spacing={2}>
                        <Grid item md={4} xs={12} className={classes.contentText}>
                          <span>Name</span>
                        </Grid>
                        <Grid item md={8} xs={12}>
                          {firstName} {lastName}
                        </Grid>
                        <Grid item md={4} xs={12} className={classes.contentText}>
                          <span>Email</span>
                        </Grid>
                        <Grid item md={8} xs={12}>
                          {user?.email}
                        </Grid>
                        <Grid item md={4} xs={12} className={classes.contentText}>
                          <span>Mobile Number</span>
                        </Grid>
                        <Grid item md={8} xs={12}>
                          +{countryCode} {mobileNumber}
                        </Grid>
                      </Grid>
                    </div>
                    <div style={{ padding: "0 0.5rem", float: "right" }}>
                      <Button color="primary" onClick={handleClose} data-testid="confirmCancel">
                        <span>Cancel</span>
                      </Button>
                      <Button className={classes.btn_primary} onClick={onConfirm} data-testid="confirmSubmit">
                        <span>Confirm</span>
                      </Button>
                    </div>
                  </Box>
                </Modal>
              </Grid>
            </Grid>
          </div>
        </div>
      </PublicWrapper>
    </div>
  );
};

export default ContactInfo;
